import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ExclamationTriangleIcon,
  CloseBoldIcon,
  CloseIcon,
  OverlayTriggerTooltip,
  ImageIcon,
  RepromptIcon,
  SerifXIcon,
} from '@utilities';

import {
  createImageObject,
  IMAGE_UPLOAD_MAX_FILE_SIZE,
  uploadImageToS3,
} from '@api/instant_survey';

import styles from './_index.module.scss';

const ConceptDetails = ({ group, isReadOnly, onUpdate }) => {
  const [imageError, setImageError] = useState(null);
  const [isEditingVariable, setIsEditingVariable] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);

  const fileInputRef = useRef(null);
  const inputWrapperRef = useRef(null);

  useEffect(() => {
    if (!isEditingVariable) return;
    const handleClickOutside = (e) => {
      if (
        isEditingVariable &&
        inputWrapperRef.current &&
        !inputWrapperRef.current.contains(e.target)
      ) {
        if (!group?.conceptName?.trim()) setIsEditingVariable(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditingVariable, group?.conceptName]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > IMAGE_UPLOAD_MAX_FILE_SIZE) {
      setImageError({ errorMsg: 'File size exceeds 5MB limit' });
      return;
    }

    setImageError(null);
    setIsImageLoading(true);

    try {
      const imageObject = await createImageObject({ objectType: file.type.substring(6) });

      if (!imageObject.url.startsWith('http://s3mock')) {
        await uploadImageToS3({
          fields: imageObject.fields,
          file,
          url: imageObject.url,
        });
      }

      // Store a preview URL until the backend returns a cdnUrl
      onUpdate({ conceptImage: { id: imageObject.id, previewUrl: URL.createObjectURL(file) } });
    } catch (error) {
      setImageError({ errorMsg: 'please try again.' });
      console.error('Error uploading image:', error);
    } finally {
      setIsImageLoading(false);
    }
  };

  const handleFileRemove = () => {
    onUpdate({ conceptImage: null });
  };

  const handleInputChange = (value) => {
    onUpdate({ conceptName: value });
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Use previewUrl if present, otherwise cdnUrl
  const imageUrl = group?.conceptImage?.cdnUrl || group?.conceptImage?.previewUrl;

  return (
    <div className={styles['concept-details']}>
      <h4>Concept Details</h4>
      <div className={styles['concept-details-input-image-container']}>
        {!imageUrl && !isImageLoading && !imageError && !isReadOnly ? (
          <div
            aria-label="Add Image"
            className={styles['concept-details-image']}
            onClick={handleClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleClick();
            }}
            role="button"
            tabIndex={0}
          >
            <ImageIcon />
            <p>
              Add an <strong>Image</strong>
            </p>
            <input
              accept="image/gif, image/jpeg, image/png"
              className={styles['concept-details-input-file']}
              disabled={isImageLoading}
              hidden
              id="concept-image-input"
              onChange={handleFileChange}
              ref={fileInputRef}
              type="file"
            />
          </div>
        ) : (
          <>
            {imageError && (
              <div className={styles['concept-details-input-image-error']}>
                <div className={styles['concept-details-input-image-error-text']}>
                  <ExclamationTriangleIcon /> Image upload failed: {imageError.errorMsg}
                </div>
                <div
                  className={styles['concept-details-input-image-error-close-icon']}
                  onClick={() => setImageError(null)}
                >
                  <CloseBoldIcon />
                </div>
              </div>
            )}

            {isImageLoading && (
              <div className={styles['concept-details-input-image-loading']}>
                <RepromptIcon /> Image is uploading...
              </div>
            )}

            {!isImageLoading && !imageError && imageUrl && (
              <div className={styles['concept-details-input-image-preview']}>
                <img alt="Uploaded File" src={imageUrl} />
                {!isReadOnly && (
                  <div className={styles['concept-details-input-remove-image-container']}>
                    <OverlayTriggerTooltip content="Remove Image" position="bottom">
                      <div
                        className={styles['concept-details-input-remove-image-button']}
                        onClick={handleFileRemove}
                      >
                        <CloseIcon className={styles['concept-details-input-remove-image-icon']} />
                      </div>
                    </OverlayTriggerTooltip>
                  </div>
                )}
                <div className={styles['concept-details-input-image-preview-overlay']} />
              </div>
            )}
          </>
        )}
      </div>

      <div className={styles['concept-details-variable']}>
        {isEditingVariable || isReadOnly || group?.conceptName ? (
          <div className={styles['concept-details-variable-input']} ref={inputWrapperRef}>
            <SerifXIcon />
            <textarea
              autoFocus
              className={styles['concept-details-input-field']}
              disabled={isReadOnly}
              id={`group-${group.clientId || group.id}-concept-name`}
              maxLength={128}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="Enter Name"
              value={group?.conceptName || ''}
            />
          </div>
        ) : (
          <div
            onClick={() => setIsEditingVariable(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setIsEditingVariable(true);
            }}
            role="button"
            tabIndex={0}
          >
            <SerifXIcon />
            <span>
              Add a <strong>Name</strong>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

ConceptDetails.propTypes = {
  group: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
  onUpdate: PropTypes.func.isRequired,
};

export default ConceptDetails;
